import React, { useState, useEffect, useMemo, useRef } from "react";
import { Button, message } from "antd";
import FacilitiesFilter from "./SampleSizeFilters";
import FacilitiesTable from "./SampleSizeTable";
import AddFacilityModal from "./AddSampleSize";
import { httpService } from "../../../../services/httpService.service";
import "../VisitBoard/VisitBoard.scss";
import "./sampleSize.scss";

const DEBOUNCE_DELAY = 350;

const SampleSize = () => {
  const [isModalVisible, setIsModalVisible] = useState(false);
  const [allFacilities, setAllFacilities] = useState([]);
  const [loading, setLoading] = useState(false);
  const [filters, setFilters] = useState({ Search: "" });
  // What the debounce has actually committed — the filtered list is derived
  // from it, so a refetch (after an add or a delete) keeps the active search
  // instead of silently showing everything again.
  const [appliedSearch, setAppliedSearch] = useState("");
  const [pagination, setPagination] = useState({ pageSize: 10, current: 1 });
  const [editingFacility, setEditingFacility] = useState(null);
  const debounceRef = useRef();

  useEffect(() => {
    fetchFacilities();
    return () => clearTimeout(debounceRef.current);
  }, []);

  const fetchFacilities = () => {
    setLoading(true);
    const projectID = localStorage.getItem("currentProjectID");

    httpService.get(
      `/api/DataChecks/getAll?projectId=${projectID}`,
      (res) => {
        setAllFacilities(res.data || []);
        setLoading(false);
      },
      (error) => {
        message.error("Failed to fetch sample sizes");
        setLoading(false);
      }
    );
  };

  // The whole filtered list goes to the Table, which slices it for the current
  // page. Slicing it here as well meant every page after a refetch was cut
  // from a stale page number, and page 2 could come back empty.
  const facilities = useMemo(() => {
    if (!appliedSearch) return allFacilities;
    return allFacilities.filter((facility) =>
      (facility.name || "").toLowerCase().includes(appliedSearch)
    );
  }, [allFacilities, appliedSearch]);

  // Deleting the last row of the last page (or narrowing a search) can leave
  // the pager pointing past the end — walk it back rather than showing blank.
  useEffect(() => {
    const lastPage = Math.max(
      1,
      Math.ceil(facilities.length / pagination.pageSize)
    );
    if (pagination.current > lastPage) {
      setPagination((prev) => ({ ...prev, current: lastPage }));
    }
  }, [facilities.length, pagination.current, pagination.pageSize]);

  const handleFiltersChange = (newFilters) => {
    setFilters((prev) => ({ ...prev, ...newFilters }));

    if (debounceRef.current) clearTimeout(debounceRef.current);
    setLoading(true);

    debounceRef.current = setTimeout(() => {
      setAppliedSearch((newFilters.Search || "").trim().toLowerCase());
      setPagination((prev) => ({ ...prev, current: 1 }));
      setLoading(false);
    }, DEBOUNCE_DELAY);
  };

  const handlePageChange = (paginationConfig) => {
    setPagination({
      current: paginationConfig.current,
      pageSize: paginationConfig.pageSize,
    });
  };

  const handleAddFacility = (values) => {
    setLoading(true);
    const projectID = localStorage.getItem("currentProjectID");

    if (editingFacility) {
      httpService.put(
        "/api/DataChecks",
        { id: editingFacility.id, name: values.name },
        () => {
          message.success("Sample size updated successfully");
          setIsModalVisible(false);
          setEditingFacility(null);
          fetchFacilities();
        },
        () => {
          message.error("Failed to update sample size");
          setLoading(false);
        }
      );
    } else {
      httpService.post(
        "/api/DataChecks",
        { name: values.name, projectId: projectID },
        () => {
          message.success("Sample size added successfully");
          setIsModalVisible(false);
          fetchFacilities();
        },
        () => {
          message.error("Failed to add sample size");
          setLoading(false);
        }
      );
    }
  };

  const handleDelete = (record) => {
    setLoading(true);
    httpService.delete(
      `/api/DataChecks?id=${record.id}`,
      null,
      () => {
        message.success("Sample Size deleted successfully");
        fetchFacilities();
      },
      () => {
        message.error("Failed to delete Sample Size");
        setLoading(false);
      }
    );
  };

  const handleEdit = (record) => {
    setEditingFacility(record);
    setIsModalVisible(true);
  };

  const handleCancel = () => {
    setIsModalVisible(false);
    setEditingFacility(null);
  };

  return (
    <div className="ip-planning-wrapper">
      <div className="tableViewHeader d-flex justify-content-between align-items-center mb-5">
        <span className="tableViewHeaderText">Additional Tools</span>
        <Button
          className="addButtonTPM"
          type="primary"
          onClick={() => {
            setEditingFacility(null);
            setIsModalVisible(true);
          }}
        >
          + Add Additional Tool
        </Button>
      </div>

      <FacilitiesFilter
        filters={filters}
        onFiltersChange={handleFiltersChange}
      />

      <FacilitiesTable
        data={facilities}
        loading={loading}
        pagination={{
          current: pagination.current,
          pageSize: pagination.pageSize,
          total: facilities.length,
          showSizeChanger: true,
          showTotal: (total) => `${total} tools`,
        }}
        onPageChange={handlePageChange}
        onEdit={handleEdit}
        onDelete={handleDelete}
        onPreview={() => {}}
      />

      <AddFacilityModal
        visible={isModalVisible}
        onCancel={handleCancel}
        onOk={handleAddFacility}
        facility={editingFacility}
      />
    </div>
  );
};

export default SampleSize;
